import React, { useState, useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Label } from 'src/components/ui/label';
import { SpinnerIcon } from '@phosphor-icons/react';
import config from 'src/config';
import { CompanyDTO } from 'src/api/core/companies/useCompany';
import useEnableModule from 'src/api/core/modules/useEnableModule';
import useDisableModule from 'src/api/core/modules/useDisableModule';

// Import from shared types (with fallback for now)
const MODULE_TYPES = {
  REBATES: 'rebates',
} as const;

const ENTITY_TYPES = {
  COMPANY: 'company',
} as const;

interface CompanyModulesTabProps {
  company: CompanyDTO;
  onRefresh?: () => void;
}

export function CompanyModulesTab({
  company,
  onRefresh,
}: CompanyModulesTabProps) {
  const [enabledModules, setEnabledModules] = useState<Set<string>>(new Set());
  const [pendingModules, setPendingModules] = useState<Set<string>>(new Set());
  const [modulesLoading, setModulesLoading] = useState(false);
  const [hasModuleChanges, setHasModuleChanges] = useState(false);

  const enableModuleMutation = useEnableModule();
  const disableModuleMutation = useDisableModule();

  // Helper function to check if two sets are equal
  const areSetsEqual = (setA: Set<string>, setB: Set<string>): boolean => {
    if (setA.size !== setB.size) {
      return false;
    }
    for (const item of setA) {
      if (!setB.has(item)) {
        return false;
      }
    }
    return true;
  };

  // Helper function to get module descriptions
  const getModuleDescription = (moduleType: string): string => {
    switch (moduleType) {
      case MODULE_TYPES.REBATES:
        return 'Enable rebate agreements and calculations for this company';
      default:
        return 'Module functionality';
    }
  };

  // Function to save all module changes
  const handleSaveModules = async () => {
    try {
      // Determine what changes need to be made
      const toEnable = Array.from(pendingModules).filter(
        (module) => !enabledModules.has(module),
      );
      const toDisable = Array.from(enabledModules).filter(
        (module) => !pendingModules.has(module),
      );

      // Make API calls for each change
      const promises = [];

      for (const moduleType of toEnable) {
        promises.push(
          enableModuleMutation.mutateAsync({
            entityType: ENTITY_TYPES.COMPANY,
            entityId: company.id,
            moduleName: moduleType,
          }),
        );
      }

      for (const moduleType of toDisable) {
        promises.push(
          disableModuleMutation.mutateAsync({
            entityType: ENTITY_TYPES.COMPANY,
            entityId: company.id,
            moduleName: moduleType,
          }),
        );
      }

      // Wait for all API calls to complete
      await Promise.all(promises);

      // Update the current state to match pending state
      setEnabledModules(new Set(pendingModules));
      setHasModuleChanges(false);

      // Refresh parent component if callback provided
      onRefresh?.();
    } catch (error) {
      console.error('Error saving module changes:', error);
      // Reset to current state on error
      setPendingModules(new Set(enabledModules));
      setHasModuleChanges(false);
    }
  };

  // Function to cancel module changes (now Restore)
  const handleCancelModules = () => {
    setPendingModules(new Set(enabledModules));
    setHasModuleChanges(false);
  };

  // useEffect to load enabled modules
  useEffect(() => {
    const loadEnabledModules = async () => {
      setModulesLoading(true);
      try {
        const response = await fetch(
          `${config.apiBaseUrl}/core/modules/enabled?entityType=${ENTITY_TYPES.COMPANY}&entityId=${company.id}`,
          {
            method: 'GET',
            credentials: 'include',
          },
        );

        if (response.ok) {
          const data = await response.json();
          const moduleSet = new Set<string>(data.data);
          setEnabledModules(moduleSet);
          setPendingModules(new Set(moduleSet)); // Initialize pending with current state
        } else {
          console.error('Failed to load enabled modules');
        }
      } catch (error) {
        console.error('Error loading enabled modules:', error);
      } finally {
        setModulesLoading(false);
      }
    };

    if (company.id) {
      loadEnabledModules();
    }
  }, [company.id]);

  // Update hasModuleChanges when pendingModules changes
  useEffect(() => {
    setHasModuleChanges(!areSetsEqual(pendingModules, enabledModules));
  }, [pendingModules, enabledModules]);

  return (
    <div className="p-6">
      <h2 className="text-xl font-medium mb-6">Company Modules</h2>
      {modulesLoading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <SpinnerIcon className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div>
          {Object.entries(MODULE_TYPES).map(([key, value]) => {
            return (
              <div
                key={key}
                className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg mb-4 w-full"
              >
                <Checkbox
                  checked={pendingModules.has(value)}
                  onCheckedChange={(checked) => {
                    const newPendingModules = new Set(pendingModules);
                    if (checked) {
                      newPendingModules.add(value);
                    } else {
                      newPendingModules.delete(value);
                    }
                    setPendingModules(newPendingModules);
                  }}
                  disabled={
                    modulesLoading ||
                    enableModuleMutation.isLoading ||
                    disableModuleMutation.isLoading
                  }
                />
                <Label className="flex-1">
                  <div>
                    <div className="text-sm font-medium">
                      {key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()}
                    </div>
                    <div className="text-xs text-gray-600">
                      {getModuleDescription(value)}
                    </div>
                  </div>
                </Label>
              </div>
            );
          })}
          {/* Save/Restore buttons for module changes */}
          <div className="flex justify-end gap-4 mt-6 pt-6">
            <Button
              variant="outline"
              onClick={handleCancelModules}
              disabled={
                !hasModuleChanges ||
                enableModuleMutation.isLoading ||
                disableModuleMutation.isLoading
              }
            >
              Restore
            </Button>
            <Button
              onClick={handleSaveModules}
              disabled={
                !hasModuleChanges ||
                enableModuleMutation.isLoading ||
                disableModuleMutation.isLoading
              }
            >
              {enableModuleMutation.isLoading || disableModuleMutation.isLoading
                ? 'Saving...'
                : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
